import { useLazyQuery } from "@apollo/client/react";
import { alert } from "@components/common/Alert";
import { GET_AUTO_TEMPLATE_BY_UUID } from "@graphql/queries/AutoTemplates";
import { useState } from "react";

import { processField } from "../TemplateBuilder/utils";

/**
 * Shared hook for fetching a user template by UUID and
 * returning its processed fields + metadata.
 *
 * Returns { loadUserTemplate, isLoadingTemplate } where
 * loadUserTemplate(uuid) resolves with { templateType, fields, userPreferences }
 * or null on failure.
 */
export function useLoadUserTemplate() {
  const [fetchTemplate] = useLazyQuery(GET_AUTO_TEMPLATE_BY_UUID);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);

  const loadUserTemplate = async (uuid) => {
    setIsLoadingTemplate(true);
    try {
      const { data } = await fetchTemplate({
        variables: { uuid },
      });
      const template = data?.autoTemplateById;
      if (!template) return null;

      const fields = (template.fields || [])
        .map((f) =>
          processField(
            f.fieldName || "",
            f.fieldValue || "",
            f.fieldIdx || 0,
          ),
        )
        .sort((a, b) => a.fieldIdx - b.fieldIdx);

      return {
        templateType: template.templateType,
        fields,
        userPreferences: template.userPreferences ?? "",
      };
    } catch {
      alert("error", "Failed to load template. Please try again.");
      return null;
    } finally {
      setIsLoadingTemplate(false);
    }
  };

  return { loadUserTemplate, isLoadingTemplate };
}
